import { createApp } from 'vue'
import App from './App.vue'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import './assets/global.css'
import router from './router'
import { createPinia } from 'pinia'
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import { useUserStore } from '@/store/modules/user'
import { EventEmitter } from 'events'

// 设置EventEmitter的最大监听器数量
EventEmitter.defaultMaxListeners = 20

// URL 规范化（/login?token=... → /#/login?token=...）已移至 router/index.js，在 createRouter 之前执行

// 添加 ResizeObserver 错误处理
const originalResizeObserver = window.ResizeObserver
window.ResizeObserver = class ResizeObserver extends originalResizeObserver {
  constructor(callback) {
    super((entries, observer) => {
      try {
        callback(entries, observer)
      } catch (error) {
        if (error.message && error.message.includes('ResizeObserver')) {
          return
        }
        throw error
      }
    })
  }
}

const app = createApp(App)
const pinia = createPinia()

// 修改 Element Plus 配置
app.use(ElementPlus, {
  locale: zhCn,
  size: 'default',
  zIndex: 2000
})

// 添加全局样式（颜色/圆角/阴影等统一引自 tokens.css 语义令牌，勿在此硬编码魔法值）
// 说明：Element Plus 组件色彩由下方 :root 的 --el-* 变量承载，均映射自 tokens.css。
// 其余对 popper/date/table 等的覆盖保留 !important 兜底，但色值一律改用 var(--...) 引用。
const themeStyle = document.createElement('style')
themeStyle.textContent = `
  :root {
    --el-color-primary: var(--color-primary);
    --el-color-primary-light-3: var(--color-primary-lighter);
    --el-color-primary-light-5: var(--color-primary-soft);
    --el-color-primary-light-7: var(--color-primary-softer);
    --el-color-primary-light-8: var(--color-primary-faint);
    --el-color-primary-light-9: #f0fdfe;
    --el-color-primary-dark-2: var(--color-primary-dark);
    --el-bg-color: var(--color-bg-raised);
    --el-bg-color-page: var(--color-bg-page);
    --el-text-color-primary: var(--color-text-primary);
    --el-text-color-regular: var(--color-text-regular);
    --el-text-color-secondary: var(--color-text-secondary);
    --el-text-color-placeholder: var(--color-text-placeholder);
    --el-border-color: var(--color-border);
    --el-border-color-light: var(--color-border-light);
    --el-border-color-lighter: var(--color-border-lighter);
    --el-border-color-extra-light: var(--color-border-extra);
    --el-fill-color: var(--color-bg-raised);
    --el-fill-color-light: var(--color-bg-input);
    --el-fill-color-lighter: var(--color-bg-fill2);
    --el-fill-color-extra-light: var(--color-bg-fill3);
    --el-fill-color-dark: var(--color-bg-fill2);
    --el-fill-color-darker: var(--color-bg-fill3);
    --el-fill-color-blank: var(--color-bg-raised);
    --el-border-radius-base: var(--radius-sm);
  }
  .el-card {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
    color: var(--color-text-primary) !important;
  }
  .el-card__header {
    border-bottom-color: var(--color-border) !important;
    color: var(--color-text-primary) !important;
  }
  .el-input__inner {
    background-color: var(--color-bg-input) !important;
    border-color: var(--color-border) !important;
    color: var(--color-text-primary) !important;
  }
  .el-input__inner::placeholder {
    color: var(--color-text-placeholder) !important;
    background-color: transparent !important;
  }
  /* Element Plus 新版本输入框样式 */
  .el-input__wrapper {
    background-color: var(--color-bg-input) !important;
    border-color: var(--color-border) !important;
  }
  .el-input__wrapper input::placeholder,
  .el-input__wrapper textarea::placeholder {
    color: var(--color-text-placeholder) !important;
    background-color: transparent !important;
  }
  .el-select__wrapper {
    background-color: var(--color-bg-input) !important;
    border-color: var(--color-border) !important;
  }
  .el-select__placeholder {
    color: var(--color-text-placeholder) !important;
    background-color: transparent !important;
  }
  .el-select__input {
    color: var(--color-text-primary) !important;
  }
  .el-select__input::placeholder {
    color: var(--color-text-placeholder) !important;
    background-color: transparent !important;
  }
  .el-select .el-input__inner {
    background-color: var(--color-bg-input) !important;
    border-color: var(--color-border) !important;
    color: var(--color-text-primary) !important;
  }
  .el-select .el-input__inner::placeholder {
    color: var(--color-text-placeholder) !important;
    background-color: transparent !important;
  }
  .el-textarea__inner {
    background-color: var(--color-bg-input) !important;
    border-color: var(--color-border) !important;
    color: var(--color-text-primary) !important;
  }
  .el-textarea__inner::placeholder {
    color: var(--color-text-placeholder) !important;
    background-color: transparent !important;
  }
  /* 下拉框背景色 */
  .el-select-dropdown {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
  }
  .el-select-dropdown__item {
    background-color: var(--color-bg-raised) !important;
    color: var(--color-text-primary) !important;
  }
  .el-select-dropdown__item:hover {
    background-color: var(--color-hover-bg) !important;
    color: var(--color-primary-light) !important;
  }
  .el-select-dropdown__item.is-selected {
    background-color: var(--color-active-bg) !important;
    color: var(--color-primary-light) !important;
    font-weight: var(--font-weight-bold);
  }
  .el-select-dropdown__item.is-disabled {
    color: var(--color-text-placeholder) !important;
    opacity: 0.5;
  }
  /* 下拉菜单空状态 */
  .el-select-dropdown__empty {
    color: var(--color-text-secondary) !important;
  }
  /* 其他下拉框（如 el-dropdown-menu） */
  .el-dropdown-menu {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
  }
  .el-dropdown-menu__item {
    color: var(--color-text-primary) !important;
  }
  .el-dropdown-menu__item:hover {
    background-color: var(--color-hover-bg) !important;
    color: var(--color-primary-light) !important;
  }
  .el-dropdown-menu__item.is-disabled {
    color: var(--color-text-placeholder) !important;
    opacity: 0.5;
  }
  /* Element Plus 水平菜单下拉框的 popper 样式 */
  .el-menu--horizontal .el-sub-menu__popper,
  .el-sub-menu__popper,
  .el-popper.is-dark,
  .el-popper[data-popper-placement] {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
  }
  .el-menu--horizontal .el-sub-menu__popper .el-menu,
  .el-sub-menu__popper .el-menu,
  .el-popper .el-menu {
    background-color: var(--color-bg-raised) !important;
    border: none !important;
  }
  .el-menu--horizontal .el-sub-menu__popper .el-menu-item,
  .el-sub-menu__popper .el-menu-item,
  .el-popper .el-menu-item {
    background-color: var(--color-bg-raised) !important;
    color: var(--color-text-regular) !important;
  }
  .el-menu--horizontal .el-sub-menu__popper .el-menu-item:hover,
  .el-sub-menu__popper .el-menu-item:hover,
  .el-popper .el-menu-item:hover {
    background-color: var(--color-hover-bg) !important;
    color: var(--color-primary-light) !important;
  }
  .el-menu--horizontal .el-sub-menu__popper .el-menu-item.is-active,
  .el-sub-menu__popper .el-menu-item.is-active,
  .el-popper .el-menu-item.is-active {
    background-color: var(--color-active-bg) !important;
    color: var(--color-primary-light) !important;
  }
  /* 通用 popper 背景色（用于所有通过 popper 显示的下拉框） */
  .el-popper {
    background-color: var(--color-bg-raised) !important;
  }
  .el-popper__arrow::before {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
  }
  /* 日期选择器下拉框 */
  .el-picker__popper {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
  }
  .el-date-picker {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
    color: var(--color-text-primary) !important;
  }
  .el-date-picker__header-label {
    color: var(--color-text-primary) !important;
  }
  .el-picker-panel {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
  }
  .el-picker-panel__content {
    color: var(--color-text-primary) !important;
  }
  .el-date-table td {
    color: var(--color-text-primary) !important;
  }
  .el-date-table td.available:hover {
    color: var(--color-primary-light) !important;
  }
  .el-date-table td.current:not(.disabled) {
    color: var(--color-primary-light) !important;
  }
  .el-date-table td.today span {
    color: var(--color-primary-light) !important;
    font-weight: var(--font-weight-bold);
  }
  /* 时间选择器 */
  .el-time-panel {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
  }
  .el-time-spinner__item {
    color: var(--color-text-primary) !important;
  }
  .el-time-spinner__item:hover:not(.disabled):not(.active) {
    background-color: var(--color-hover-bg) !important;
    color: var(--color-primary-light) !important;
  }
  .el-time-spinner__item.active:not(.disabled) {
    color: var(--color-primary-light) !important;
    font-weight: var(--font-weight-bold);
  }
  /* 级联选择器 */
  .el-cascader-menu {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
  }
  .el-cascader-menu__item {
    color: var(--color-text-primary) !important;
  }
  .el-cascader-menu__item:hover {
    background-color: var(--color-hover-bg) !important;
    color: var(--color-primary-light) !important;
  }
  .el-cascader-menu__item.is-active {
    color: var(--color-primary-light) !important;
    font-weight: var(--font-weight-bold);
  }
  /* 自动完成下拉框 */
  .el-autocomplete-suggestion {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
  }
  .el-autocomplete-suggestion__list .el-autocomplete-suggestion__item {
    color: var(--color-text-primary) !important;
  }
  .el-autocomplete-suggestion__list .el-autocomplete-suggestion__item:hover {
    background-color: var(--color-hover-bg) !important;
    color: var(--color-primary-light) !important;
  }
  .el-autocomplete-suggestion__list .el-autocomplete-suggestion__item.highlighted {
    background-color: var(--color-active-bg) !important;
    color: var(--color-primary-light) !important;
  }
  .el-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2000;
    height: 100%;
    background-color: rgba(0,0,0,0.5);
    overflow: auto;
  }
  /* Electron 下分享分镜弹窗：遮罩可见但弹窗内容不显示；且 left 可能异常大导致跑出视口，强制限制在视口内并居中 */
  .share-storyboard-overlay.el-overlay {
    left: 0 !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
  }
  .share-storyboard-overlay.el-overlay .el-overlay-dialog,
  .share-storyboard-overlay .el-overlay-dialog {
    opacity: 1 !important;
    transform: none !important;
    animation: none !important;
    left: 0 !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }
  .share-storyboard-overlay .share-storyboard-dialog.el-dialog {
    opacity: 1 !important;
    visibility: visible !important;
    display: flex !important;
    background-color: var(--color-bg-raised) !important;
    margin: 0 !important;
    position: relative !important;
    left: auto !important;
    right: auto !important;
  }
  .el-dialog {
    background-color: var(--color-bg-raised) !important;
    color: var(--color-text-primary) !important;
  }
  .el-dialog__header {
    border-bottom-color: var(--color-border) !important;
  }
  .el-dialog__title {
    color: var(--color-text-primary) !important;
  }
  .el-table {
    background-color: var(--color-bg-raised) !important;
    color: var(--color-text-primary) !important;
    --el-table-bg-color: var(--color-bg-raised);
    --el-table-tr-bg-color: var(--color-bg-raised);
    --el-table-header-bg-color: var(--color-bg-input);
    --el-table-row-hover-bg-color: var(--color-bg-fill2);
  }
  .el-table__body,
  .el-table__body tr,
  .el-table__body td.el-table__cell,
  .el-table__body .el-table__cell {
    background-color: var(--color-bg-raised) !important;
  }
  .el-table__body tr.el-table__row--striped td.el-table__cell,
  .el-table--striped .el-table__body tr.el-table__row--striped td.el-table__cell {
    background-color: var(--color-bg-input) !important;
  }
  .el-table__body tr:hover > td.el-table__cell {
    background-color: var(--color-bg-fill2) !important;
  }
  .el-table th.el-table__cell {
    background-color: var(--color-bg-input) !important;
    color: var(--color-text-primary) !important;
    border-bottom-color: var(--color-border) !important;
  }
  .el-table td.el-table__cell {
    border-bottom-color: var(--color-border-lighter) !important;
  }
  .el-table--border {
    border-color: var(--color-border) !important;
  }
  .el-table--border::after,
  .el-table--border::before {
    background-color: var(--color-border) !important;
  }
  .el-table--border th.el-table__cell,
  .el-table--border td.el-table__cell {
    border-right-color: var(--color-border) !important;
  }
  /* 表格 tooltip 样式 - 确保文字颜色与背景色有足够对比度 */
  .el-tooltip__popper,
  .el-tooltip__popper.is-dark,
  .el-popper[role="tooltip"],
  .el-popper[role="tooltip"].is-dark {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
    color: var(--color-text-primary) !important;
  }
  .el-tooltip__popper *,
  .el-tooltip__popper.is-dark *,
  .el-popper[role="tooltip"] *,
  .el-popper[role="tooltip"].is-dark *,
  .el-tooltip__popper .el-tooltip__content,
  .el-tooltip__popper.is-dark .el-tooltip__content,
  .el-popper[role="tooltip"] .el-tooltip__content,
  .el-popper[role="tooltip"].is-dark .el-tooltip__content {
    color: var(--color-text-primary) !important;
  }
  .el-tooltip__popper .el-tooltip__arrow::before,
  .el-tooltip__popper.is-dark .el-tooltip__arrow::before,
  .el-popper[role="tooltip"] .el-tooltip__arrow::before,
  .el-popper[role="tooltip"].is-dark .el-tooltip__arrow::before {
    background-color: var(--color-bg-raised) !important;
    border-color: var(--color-border) !important;
  }
  /* 调整全局消息提示位置，避免遮住菜单 */
  .el-message {
    top: 80px !important;
  }
  .el-message--success,
  .el-message--error,
  .el-message--warning,
  .el-message--info {
    top: 80px !important;
  }
  /* 表单标签：深色底 + 高对比文字，避免与底色冲突 */
  .el-form-item__label,
  .el-form-item__content .el-form-item__label,
  label.el-form-item__label {
    color: var(--color-text-primary) !important;
    background-color: transparent !important;
    -webkit-text-fill-color: initial;
  }
  .el-form-item.is-required:not(.is-no-asterisk) > .el-form-item__label::before {
    color: var(--color-danger) !important;
  }
`
document.head.appendChild(themeStyle)

app.use(router)
app.use(pinia)

// 全局捕获 Vue 组件/路由错误，便于 Electron 下排查白屏（控制台会打出报错）
app.config.errorHandler = (err, instance, info) => {
  console.error('[Vue Error]', info, err)
  if (err && err.stack) console.error(err.stack)
}

// 确保用户存储初始化后再挂载应用
const userStore = useUserStore()
// 确保用户名正确
userStore.initializeUserStore()

app.mount('#app')

// 添加全局错误处理，忽略ResizeObserver相关警告
const originalConsoleError = console.error;
console.error = function(msg, ...args) {
  if (msg && typeof msg === 'string' && msg.includes('ResizeObserver')) {
    // 忽略ResizeObserver相关警告
    return;
  }
  originalConsoleError.apply(console, [msg, ...args]);
};

// 添加全局错误处理函数
window.onerror = function(message, source, lineno, colno, error) {
  if (message && message.toString().includes('ResizeObserver')) {
    // 忽略ResizeObserver相关错误
    return true; // 阻止错误显示在控制台
  }
  return false; // 让其他错误正常显示
};

// 添加全局未捕获的 Promise 错误处理
window.addEventListener('unhandledrejection', function(event) {
  const reason = event.reason
  const errorMessage = reason?.message || reason?.toString() || 'Unknown error'
  
  // 忽略 ResizeObserver 相关错误
  if (errorMessage.includes('ResizeObserver loop completed with undelivered notifications') ||
      errorMessage.includes('ResizeObserver loop limit exceeded')) {
    event.preventDefault()
    return
  }
  
  // 记录其他未捕获的 Promise rejection，但不阻止页面崩溃
  console.error('Unhandled Promise Rejection:', {
    reason: reason,
    message: errorMessage,
    stack: reason?.stack,
    name: reason?.name
  })
  
  // 如果是 Electron API 相关的错误，尝试恢复
  if (errorMessage.includes('electronAPI') || errorMessage.includes('Cannot read properties')) {
    console.warn('Electron API error detected, attempting to recover...')
    // 不阻止错误传播，让组件自己处理
  }
  
  // 如果是下载相关的错误，记录详细信息
  if (errorMessage.includes('下载') || errorMessage.includes('download') || errorMessage.includes('fetch')) {
    console.error('下载相关错误:', {
      reason: reason,
      message: errorMessage,
      stack: reason?.stack
    })
  }
})
